import View from '../Support/View/View';

export default class Field extends View {
    #$groupEl    = null;
    #$wrapperEl  = null;
    #$feedbackEl = null;
    #$helperEl   = null;
    #$errorEl    = null;

    static get viewName() {
        return 'field';
    }
    static get viewType() {
        return 'component';
    }

    start() {
        this.addEventHandler('input', () => this.clearError());

        this.$el.addEventListener('focus', this.#onFocus.bind(this));
        this.$el.addEventListener('blur', this.#onBlur.bind(this));

        this.#setupFloatingEffect();
        this.#setupFieldTypeSpecifics();
    }
    destroy() {
        this.$el.removeEventListener('focus', this.#onFocus.bind(this));
        this.$el.removeEventListener('blur', this.#onBlur.bind(this));
    }

    /**
     * @returns {string}
     */
    get id() {
        return this.$el.id;
    }

    /**
     * @returns {string}
     */
    get name() {
        return this.$el.name;
    }

    /**
     * @returns {int|string|null}
     */
    get value() {
        return this.$el.value || null;
    }

    /**
     * @returns {FileList|null}
     */
    get files() {
        if (this.type !== 'file') {
            return null;
        }
        if (!this.$el.files.length) {
            return null;
        }

        return this.$el.files;
    }

    /**
     * @param {int|string} value
     */
    set value(value) {
        this.clearError();

        this.$el.value = value;
    }

    /**
     * @returns {string}
     */
    get type() {
        /**
         * Splitting because :
         *     - select:not([multiple]) --> select-one
         *     - select:[multiple] --> select-multiple
         */
        return (this.$el.type || '').split('-')[0];
    }

    /**
     * @returns {HTMLLabelElement|null}
     */
    get label() {
        return this.$el.labels[0] || null;
    }

    /**
     * @returns {string}
     */
    get placeholder() {
        return this.$el.placeholder;
    }

    /**
     * @returns {int|string}
     */
    get min() {
        return this.$el.min;
    }

    /**
     * @returns {int|string}
     */
    get max() {
        return this.$el.max;
    }

    /**
     * @returns {string}
     */
    get autocomplete() {
        return this.$el.autocomplete;
    }

    /**
     * @returns {boolean}
     */
    get autofocus() {
        return !!this.$el.autofocus;
    }

    /**
     * @returns {HTMLElement}
     */
    get group() {
        if (!this.#$groupEl) {
            this.#$groupEl = document.querySelector(`#field-group-${this.id}`);
        }

        return this.#$groupEl;
    }

    /**
     * @returns {HTMLElement|null}
     */
    get wrapper() {
        if (!this.#$wrapperEl) {
            this.#$wrapperEl = this.group.querySelector('.field-wrapper');
        }

        return this.#$wrapperEl || null;
    }

    /**
     * @returns {HTMLElement|null}
     */
    get feedback() {
        if (!this.#$feedbackEl) {
            this.#$feedbackEl = this.group.querySelector(`#field-feedback-${this.id}`);
        }

        return this.#$feedbackEl || null;
    }

    /**
     * @returns {HTMLElement|null}
     */
    get helper() {
        if (!this.#$helperEl) {
            this.#$helperEl = this.group.querySelector(`#field-helper-${this.id}`);
        }

        return this.#$helperEl || null;
    }

    /**
     * @returns {boolean}
     */
    get valid() {
        return !this.#$errorEl;
    }

    /**
     * @returns {HTMLElement|null}
     */
    get error() {
        if (!this.group.classList.contains('is-invalid')) {
            return null;
        }

        if (!this.#$errorEl) {
            this.#$errorEl = this.group.querySelector(`#field-feedback-${this.id}`);
        }

        return this.#$errorEl || null;
    }

    /**
     * @param {string|null}
     */
    set error(message) {
        this.setError(message);
    }

    /**
     * @returns {boolean}
     */
    get isRequired() {
        return !!this.$el.required;
    }

    /**
     * @returns {boolean}
     */
    get isDisabled() {
        return !!this.$el.disabled;
    }

    /**
     * @returns {boolean}
     */
    get isChecked() {
        return !!this.$el.checked;
    }

    /**
     * @returns {boolean}
     */
    get isReadOnly() {
        return !!this.$el.readOnly;
    }

    /**
     * @returns {boolean}
     */
    get allowsMultiple() {
        return !!this.$el.multiple;
    }

    /**
     * @returns {boolean}
     */
    get isEmpty() {
        return !(this.value || '').trim();
    }

    /**
     * @returns {boolean}
     */
    get isNotEmpty() {
        return !this.isEmpty;
    }

    /**
     * @returns {boolean}
     */
    get isFilled() {
        return this.isNotEmpty;
    }

    /**
     * @param {string|null} message If a string, set's the error message, else, removes it.
     */
    setError(message) {
        const feedbackId = `field-feedback-${this.id}`;

        if (!this.#$errorEl) {
            this.#$errorEl = this.group.querySelector(`#${feedbackId}`);
        }

        if (message == null) {
            if (this.#$errorEl) {
                this.#$errorEl.remove();
                this.#$errorEl    = null;
            }

            this.$el.removeAttribute('aria-describedby');
            this.$el.removeAttribute('aria-errormessage');
            this.$el.removeAttribute('aria-invalid');

            this.group.classList.replace('is-invalid', 'is-valid');

            this.#$feedbackEl = null;

            const event = new CustomEvent('view:field.error', {
                bubbles: true,
                delegateTarget: this.$el,
                detail: {
                    hasError: false,
                    error: null,
                },
            });

            this.dispatchEvent(event);

            return;
        }

        if (!this.#$errorEl) {
            this.#$errorEl = document.createElement('small');

            this.#$errorEl.id          = feedbackId;
            this.#$errorEl.className   = 'field-feedback';
            this.#$errorEl.setAttribute('aria-live', 'assertive');

            this.group.appendChild(this.#$errorEl);
        }

        this.#$errorEl.textContent = message;

        this.$el.setAttribute('aria-describedby', feedbackId);
        this.$el.setAttribute('aria-errormessage', feedbackId);
        this.$el.setAttribute('aria-invalid', true);

        this.group.classList.replace('is-valid', 'is-invalid');

        this.#$feedbackEl = null;

        const event = new CustomEvent('view:field.error', {
            bubbles: true,
            delegateTarget: this.$el,
            detail: {
                hasError: true,
                error: this.#$errorEl,
            },
        });

        this.dispatchEvent(event);
    }

    /**
     * @returns {void}
     */
    clearError() {
        this.setError(null);
    }

    /**
     * @returns {void}
     */
    reset() {
        this.clearError();
        this.#removeFloatingEffect();

        if (['radio', 'checkbox'].includes(this.type)) {
            this.$el.checked = false;

            return;
        }

        if (this.type === 'file') {
            this.wrapper?.setAttribute('data-caption', this.dataset.caption || '');
        }

        this.$el.value = '';
    }

    /**
     * @param {FocusEvent} e
     */
    #onFocus(e) {
        this.triggerEventHandlers(e);
    }

    /**
     * @param {FocusEvent} e
     */
    #onBlur(e) {
        this.triggerEventHandlers(e);
    }

    #setupFloatingEffect() {
        if (!this.group.classList.contains('has-floating-effect')) {
            return;
        }

        if (this.value) {
            this.#setFloatingEffect();
        }

        this.addEventHandler('focus', () => this.#setFloatingEffect());
        this.addEventHandler('blur', ({ target: { value } }) => {
            if (value) {
                return;
            }

            this.#removeFloatingEffect();
        });
    }

    #setFloatingEffect() {
        this.group.classList.add('is-floating');

        if (this.dataset.placeholder) {
            this.$el.placeholder = this.dataset.placeholder;
        }
    }

    #removeFloatingEffect() {
        if (this.placeholder) {
            this.$el.removeAttribute('placeholder');
        }

        this.group.classList.remove('is-floating');
    }

    #setupFieldTypeSpecifics() {
        if (this.type === 'file') {
            this.#isFile();
        }
    }

    #isFile() {
        this.addEventHandler('focus', () => this.group?.classList?.add('is-focused'));
        this.addEventHandler('blur', () => this.group?.classList?.remove('is-focused'));

        if (this.dataset.caption) {
            this.wrapper?.setAttribute('data-caption', this.dataset.caption);
        }

        this.addEventHandler('input', () => {
            if (this.$el.files.length === 1) {
                this.wrapper?.setAttribute('data-caption', this.$el.files[0].name);

                return;
            }
            if (this.$el.files.length > 1) {
                this.wrapper?.setAttribute('data-caption', this.dataset.multipleCaption?.replace('{count}', this.$el.files.length));

                return;
            }

            this.wrapper?.setAttribute('data-caption', this.dataset.caption);
        });
    }
}